import { useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, ProgressBar, Row, Spinner, Table } from 'react-bootstrap'
import { useQuery } from 'react-query'

import api from '../../services/api'

/**
 * Lo que el contador contrató, visto por él mismo.
 *
 * Existe sobre todo por el cupo: cuando ya no puede agregar un contribuyente, esta pantalla es
 * la única respuesta a «¿por qué?». Sin ella, esa pregunta llega a soporte — y la respuesta
 * («diste de baja a uno y creaste otro, y eso gastó un reemplazo») es imposible de adivinar.
 */
const NIVEL_VARIANTE = { bloqueo: 'danger', critico: 'danger', advertencia: 'warning', aviso: 'info' }

function fecha(valor) {
  if (!valor) return '—'
  // Las fechas llegan como YYYY-MM-DD; `new Date` las leería en UTC y restaría un día.
  const [a, m, d] = String(valor).split('-')
  return `${d}/${m}/${a}`
}

/**
 * «Quiero contratar un plan» para quien está en una prueba. Avisa al equipo, que hace el cambio
 * a mano: no hay cobro en el sistema. El backend no manda un segundo aviso en 24 h.
 */
function ContratarPlan() {
  const [enviando, setEnviando] = useState(false)
  const [respuesta, setRespuesta] = useState('')

  const solicitar = async () => {
    setEnviando(true)
    try {
      const r = await api.post('/auth/mi-licencia/solicitar-plan/')
      setRespuesta(r.data?.detail || 'Listo: te contactaremos pronto.')
    } catch {
      setRespuesta('No pudimos enviar tu solicitud. Vuelve a intentarlo en un momento.')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Alert variant="light" className="border d-flex flex-wrap align-items-center gap-3">
      <div className="flex-grow-1">
        <strong>Estás usando una prueba.</strong> Cuando quieras seguir facturando, te ayudamos a
        elegir un plan.
      </div>
      {respuesta
        ? <span data-testid="contratar-respuesta">{respuesta}</span>
        : <Button onClick={solicitar} disabled={enviando}>
            {enviando ? 'Enviando…' : 'Quiero contratar un plan'}
          </Button>}
    </Alert>
  )
}

export default function MiPlan() {
  const { data, isLoading } = useQuery(
    'mi-licencia',
    async () => (await api.get('/auth/mi-licencia/')).data,
    { refetchOnWindowFocus: true, retry: false }
  )
  const { data: historial = [] } = useQuery(
    'mi-licencia-historial',
    async () => (await api.get('/auth/mi-licencia/historial/')).data,
    { enabled: !!data?.con_licencia, retry: false }
  )

  if (isLoading) {
    return <Container className="py-5 text-center"><Spinner animation="border" /></Container>
  }

  if (!data?.con_licencia) {
    return (
      <Container className="py-4">
        <h2>Mi plan</h2>
        <Alert variant="secondary">
          {data?.mensaje || 'Este usuario no tiene una licencia asignada.'}
        </Alert>
      </Container>
    )
  }

  const { capacidad, timbres, cupo, alerta } = data
  // En la fila tiene 0 timbres y su vigencia no ha empezado: sin este caso la pantalla diría
  // «vigencia vencida» y «tu plan no consume timbres», las dos falsas.
  const enFila = data.estado === 'en_espera'
  const usadosPct = capacidad.ilimitada || !capacidad.contratada
    ? 0
    : Math.round((capacidad.usados / capacidad.contratada) * 100)
  const timbresPct = timbres.otorgados
    ? Math.round((timbres.disponibles / timbres.otorgados) * 100)
    : 0

  return (
    <Container fluid className="py-4">
      <h2 className="mb-1">Mi plan</h2>
      <p className="text-muted">Lo que incluye tu licencia y cómo vas de capacidad y timbres.</p>

      {enFila && (
        <Alert variant="info" data-testid="aviso-fila">
          <strong>Estás en el lugar {data.lugar_en_fila} de la fila de timbres.</strong> Recibirás{' '}
          {data.timbres_del_plan} timbres de cortesía y te avisaremos por correo; tus días de
          prueba empiezan a contar ese día. Mientras tanto puedes dar de alta tu contribuyente,
          cargar tu certificado y preparar tus facturas.
        </Alert>
      )}
      {!data.puede_timbrar && !enFila && (
        <Alert variant="danger">
          <strong>No puedes emitir comprobantes:</strong> {data.motivo_timbrado}
        </Alert>
      )}
      {data.prueba_terminada && (
        <Alert variant="warning" data-testid="prueba-terminada">
          <strong>Tu prueba terminó el {fecha(data.vigencia_fin)}.</strong> Puedes consultar y
          descargar tus comprobantes hasta el <strong>{fecha(data.acceso_hasta)}</strong>; ya no se
          envían por correo. Después de esa fecha se bloquea el acceso, sin borrar tu información.
        </Alert>
      )}
      {data.es_prueba && <ContratarPlan />}
      {alerta && data.puede_timbrar && (
        <Alert variant={NIVEL_VARIANTE[alerta.nivel] || 'info'}>{alerta.mensaje}</Alert>
      )}

      <Row>
        <Col lg={4} className="mb-4">
          <Card className="h-100">
            <Card.Header><strong>Plan contratado</strong></Card.Header>
            <Card.Body>
              <h4 className="mb-3">{data.plan || '—'}</h4>
              <dl className="row mb-0 small">
                <dt className="col-6">Estado</dt>
                <dd className="col-6">
                  {enFila
                    ? <Badge bg="info">en fila de timbres</Badge>
                    : data.vigente
                      ? <Badge bg="success">vigente</Badge>
                      : <Badge bg="warning" text="dark">vigencia vencida</Badge>}
                </dd>
                <dt className="col-6">Desde</dt>
                <dd className="col-6">{enFila ? 'al recibir tus timbres' : fecha(data.vigencia_inicio)}</dd>
                <dt className="col-6">Hasta</dt>
                <dd className="col-6">
                  {enFila ? '—' : fecha(data.vigencia_fin) === '—' ? 'sin vencimiento' : fecha(data.vigencia_fin)}
                </dd>
              </dl>
              <hr />
              <div className="small text-muted mb-1">Módulos incluidos</div>
              <div className="d-flex flex-wrap gap-1">
                {(data.modulos_efectivos || []).map((m) => (
                  <Badge bg="light" text="dark" key={m} className="border">{m}</Badge>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4} className="mb-4">
          <Card className="h-100">
            <Card.Header><strong>Contribuyentes</strong></Card.Header>
            <Card.Body>
              {capacidad.ilimitada ? (
                <Alert variant="info" className="mb-0">
                  Capacidad ilimitada: puedes dar de alta los contribuyentes que necesites.
                </Alert>
              ) : (
                <>
                  <h4>{capacidad.usados} <span className="text-muted fs-6">de {capacidad.contratada}</span></h4>
                  <ProgressBar now={usadosPct} className="mb-3"
                               variant={usadosPct >= 100 ? 'danger' : usadosPct >= 80 ? 'warning' : 'success'} />
                  {/* El cupo es lo que más confunde: dar de baja libera la posición, pero crear
                      uno nuevo siempre cuesta un reemplazo. Sin explicarlo, el contador cree
                      que el sistema se equivocó. */}
                  <dl className="row mb-0 small">
                    <dt className="col-8">Posiciones libres</dt>
                    <dd className="col-4 text-end">{capacidad.disponibles}</dd>
                    <dt className="col-8">Altas distintas en la vigencia</dt>
                    <dd className="col-4 text-end">{cupo.distintos} de {cupo.permitidos}</dd>
                    <dt className="col-8">Reemplazos que te quedan</dt>
                    <dd className="col-4 text-end"><strong>{cupo.disponibles}</strong></dd>
                  </dl>
                  <Alert variant="light" className="border mt-3 mb-0 small">
                    Dar de baja a un contribuyente <strong>libera su posición</strong>, pero dar de
                    alta a uno nuevo <strong>siempre consume un reemplazo</strong>. Corregir un RFC
                    que nunca facturó no cuesta nada.
                  </Alert>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4} className="mb-4">
          <Card className="h-100">
            <Card.Header><strong>Timbres</strong></Card.Header>
            <Card.Body>
              {enFila ? (
                <Alert variant="info" className="mb-0">
                  <strong>{data.timbres_del_plan} timbres en camino.</strong> Estás en el lugar{' '}
                  {data.lugar_en_fila} de la fila.
                </Alert>
              ) : timbres.otorgados === 0 ? (
                <Alert variant="info" className="mb-0">
                  Tu plan no consume timbres de una bolsa propia.
                </Alert>
              ) : (
                <>
                  <h4>{timbres.disponibles} <span className="text-muted fs-6">de {timbres.otorgados}</span></h4>
                  <ProgressBar now={timbresPct} className="mb-3"
                               variant={timbresPct <= 1 ? 'danger' : timbresPct <= 5 ? 'warning' : 'success'} />
                  <dl className="row mb-0 small">
                    <dt className="col-8">Usados</dt>
                    <dd className="col-4 text-end">{timbres.consumidos}</dd>
                  </dl>
                  <div className="small text-muted mt-3">
                    Un comprobante timbrado consume un timbre. Una cancelación no lo devuelve.
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header><strong>Historial de posiciones</strong></Card.Header>
        <Card.Body className="p-0">
          <Table hover responsive className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th>Contribuyente</th><th>RFC</th><th>Alta</th><th>Baja</th><th>Situación</th>
              </tr>
            </thead>
            <tbody>
              {historial.length === 0 && (
                <tr><td colSpan={5} className="text-center text-muted py-4">
                  Todavía no hay movimientos.
                </td></tr>
              )}
              {historial.map((o) => (
                <tr key={o.id}>
                  <td>{o.contribuyente}</td>
                  <td className="small text-muted">{o.rfc}</td>
                  <td>{fecha(o.alta_at)}</td>
                  <td>{fecha(o.baja_at)}</td>
                  <td>
                    {o.abierta
                      ? <Badge bg="success">ocupando una posición</Badge>
                      : o.exenta_cupo
                        ? <Badge bg="info">baja sin costo de reemplazo</Badge>
                        : <Badge bg="secondary">baja{o.motivo_baja ? ` — ${o.motivo_baja}` : ''}</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </Container>
  )
}
